/**
 * Rewrite a Supabase public storage URL to use the image transform endpoint
 * so we download a right-sized JPEG/WebP instead of the multi-MB original.
 *
 *   /storage/v1/object/public/<bucket>/<path>
 *     -> /storage/v1/render/image/public/<bucket>/<path>?width=...&height=...&quality=...
 *
 * For non-supabase URLs (or already-rendered URLs) we return the input unchanged.
 * Callers should still wire an `onError` that falls back to the original URL,
 * in case the project's plan doesn't have image transforms enabled.
 */
export function transformedImageUrl(
  url: string | null | undefined,
  opts: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill' } = {}
): string | null {
  if (!url) return null;
  // Already a render URL or not a supabase storage URL → pass through
  if (url.includes('/storage/v1/render/image/')) return url;
  const idx = url.indexOf('/storage/v1/object/public/');
  if (idx === -1) return url;

  const { width = 400, height, quality = 75, resize } = opts;

  // The render endpoint can produce distorted/cropped output for width-only
  // requests on square uploads. Only transform when the caller gives an
  // explicit target box; otherwise keep the original browser-scaled image.
  if (height == null) return url;

  const rewritten = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  const sep = rewritten.includes('?') ? '&' : '?';
  const resizeParam = resize ? `&resize=${resize}` : '';
  return `${rewritten}${sep}width=${width}&height=${height}&quality=${quality}${resizeParam}`;
}
